import { useEffect, useRef, useState } from 'react';

export default function FloatingLines({
  enabledWaves = ['top', 'middle', 'bottom'],
  lineCount = [8, 12, 16],
  lineDistance = [10, 8, 6],
  parallax = true,
}) {
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const animationRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const colors = [
      'rgba(102, 126, 234, 0.08)',
      'rgba(118, 75, 162, 0.06)',
      'rgba(240, 147, 251, 0.04)',
    ];

    const allLines = [];

    enabledWaves.forEach((wave, waveIndex) => {
      const count = lineCount[waveIndex] || 10;
      const distance = lineDistance[waveIndex] || 8;
      const baseY = wave === 'top' ? 0.2 : wave === 'middle' ? 0.5 : 0.8;

      for (let i = 0; i < count; i++) {
        allLines.push({
          y: canvas.height * baseY + (i - count / 2) * distance,
          amplitude: 20 + Math.random() * 30,
          frequency: 0.002 + Math.random() * 0.003,
          phase: Math.random() * Math.PI * 2,
          speed: 0.0005 + Math.random() * 0.001,
          opacity: 0.03 + Math.random() * 0.05,
          color: colors[waveIndex % colors.length],
        });
      }
    });

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      allLines.forEach((line) => {
        ctx.beginPath();
        ctx.moveTo(0, line.y);

        for (let x = 0; x <= canvas.width; x += 5) {
          let y = line.y;

          const wave1 = Math.sin(x * line.frequency + time + line.phase) * line.amplitude;
          const wave2 = Math.sin(x * line.frequency * 0.5 + time * 0.5) * line.amplitude * 0.5;
          y += wave1 + wave2;

          if (parallax && mousePos.x && mousePos.y) {
            const dx = mousePos.x - x;
            const dy = mousePos.y - line.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < 200) {
              y += (dy / dist) * (200 - dist) * 0.3;
            }
          }

          ctx.lineTo(x, y);
        }

        ctx.strokeStyle = line.color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      time += 0.02;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [enabledWaves, lineCount, lineDistance, mousePos, parallax]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}